import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TourController } from '../../engine/tour-controller.service';
import { TourStep, estimatedDurationLabel } from '../../domain/tour-definition';
import { computeTooltipPlacement, Rect, rectCenterX } from '../../engine/tour-placement';

const TOOLTIP_WIDTH = 300;
const EST_TOOLTIP_HEIGHT = 236;
const INFLATE = 8; // spotlight padding around the target, matches Flutter
const CORNER = 14;

/**
 * The always-mounted tour overlay. Renders nothing while no tour is active;
 * otherwise renders the current step's dimming spotlight (with a cutout hole
 * + animated glow), interaction-blocking hit-bands, and the glass tooltip —
 * a faithful port of Flutter's `tour_overlay.dart`.
 *
 * ─── PERFORMANCE ─────────────────────────────────────────────────────────
 * The spotlight follows its target every frame (panels slide, lists scroll),
 * so the per-frame geometry is kept OUT of Angular's global change detection:
 * plain fields, written outside the zone, rendered with a local
 * `detectChanges()` that checks this overlay only — never the 13k-line map
 * page underneath. The frame loop runs only while a tour is active, and the
 * dim/glow move with compositor-friendly transforms instead of repainting a
 * full-screen SVG mask.
 */
@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.scss',
})
export class TourOverlayComponent implements OnInit, OnDestroy {
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  controller = inject(TourController);

  @ViewChild('tooltip') tooltipRef?: ElementRef<HTMLElement>;

  // Per-frame geometry: plain fields, not signals — see the class note.
  /** Live viewport-space rect of the current target (null when anchorless). */
  rect: Rect | null = null;
  tooltipX = 0;
  tooltipY = 0;
  resolvedPlacement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
  viewW = window.innerWidth;
  viewH = window.innerHeight;

  private rafId = 0;
  private lastStepId: string | null = null;
  private fadeKey = signal(0);

  readonly step = computed<TourStep | null>(() => {
    const s = this.controller.state();
    return s ? s.tour.steps[s.stepIndex] : null;
  });

  readonly isFreeRoam = computed(() => !!this.step()?.freeRoam);
  readonly isFullScreenStep = computed(() => !this.step()?.anchorId);

  constructor() {
    // The frame loop exists only while a tour is running.
    effect(() => {
      const active = this.controller.state() !== null;
      untracked(() => (active ? this.startLoop() : this.stopLoop()));
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.stopLoop();
  }

  private startLoop(): void {
    if (this.rafId) return;
    this.zone.runOutsideAngular(() => {
      const loop = () => {
        this.update();
        this.rafId = requestAnimationFrame(loop);
      };
      this.rafId = requestAnimationFrame(loop);
    });
  }

  private stopLoop(): void {
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.rect = null;
    this.lastStepId = null;
  }

  /** Per-frame: recompute target rect + tooltip placement, following scroll. */
  private update(): void {
    const s = this.controller.state();
    if (!s) return;
    const step = s.tour.steps[s.stepIndex];
    if (step.id !== this.lastStepId) {
      this.lastStepId = step.id;
      this.zone.run(() => this.fadeKey.update((k) => k + 1));
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let nextRect: Rect | null = null;
    const el = s.targetEl;
    if (el && el.isConnected) {
      const r = el.getBoundingClientRect();
      if (r.width >= 2 && r.height >= 2) {
        nextRect = {
          left: r.left,
          top: r.top,
          right: r.right,
          bottom: r.bottom,
          width: r.width,
          height: r.height,
        };
      }
    }

    // Tooltip placement (measure the real card height once rendered).
    const tipH = this.tooltipRef?.nativeElement.offsetHeight || EST_TOOLTIP_HEIGHT;
    const tipW = TOOLTIP_WIDTH;
    let x: number;
    let y: number;
    let resolved: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
    if (nextRect) {
      const p = computeTooltipPlacement(nextRect, tipW, tipH, vw, vh, step.placement ?? 'auto');
      x = p.x;
      y = p.y;
      resolved = p.resolved === 'auto' ? 'bottom' : p.resolved;
    } else {
      x = (vw - tipW) / 2;
      y = (vh - tipH) / 2;
    }

    // Re-render only when something actually moved, and only this overlay.
    const changed =
      !rectEq(this.rect, nextRect) ||
      Math.abs(this.tooltipX - x) > 0.5 ||
      Math.abs(this.tooltipY - y) > 0.5 ||
      this.resolvedPlacement !== resolved ||
      this.viewW !== vw ||
      this.viewH !== vh;
    if (!changed) return;
    this.rect = nextRect;
    this.tooltipX = x;
    this.tooltipY = y;
    this.resolvedPlacement = resolved;
    this.viewW = vw;
    this.viewH = vh;
    this.cdr.detectChanges();
  }

  // ── Geometry helpers for the template ──
  get holeX(): number {
    return (this.rect?.left ?? 0) - INFLATE;
  }
  get holeY(): number {
    return (this.rect?.top ?? 0) - INFLATE;
  }
  get holeW(): number {
    return (this.rect?.width ?? 0) + INFLATE * 2;
  }
  get holeH(): number {
    return (this.rect?.height ?? 0) + INFLATE * 2;
  }
  holeR = CORNER;

  /** Info steps block the hole too; requireTap/allowInteract leave it open. */
  readonly blockHole = computed(() => {
    const i = this.step()?.interaction ?? 'info';
    return i !== 'requireTap' && i !== 'allowInteract';
  });

  get caretOffset(): number {
    const r = this.rect;
    if (!r) return 0;
    const tooltipCenter = this.tooltipX + TOOLTIP_WIDTH / 2;
    return rectCenterX(r) - tooltipCenter;
  }

  // ── Tooltip content ──
  readonly accent = computed(() => this.controller.state()?.tour.accentColor ?? '#007AFF');
  readonly icon = computed(() => this.controller.state()?.tour.icon ?? 'lightbulb');
  readonly stepIndex = computed(() => this.controller.state()?.stepIndex ?? 0);
  readonly stepCount = computed(() => this.controller.state()?.tour.steps.length ?? 0);
  readonly showNext = computed(() => {
    const i = this.step()?.interaction ?? 'info';
    return i === 'info' || i === 'allowInteract';
  });
  readonly nextLabel = computed(() =>
    this.stepIndex() === this.stepCount() - 1 ? 'Done' : 'Next',
  );
  readonly useBar = computed(() => this.stepCount() > 8);
  readonly dots = computed(() => Array.from({ length: this.stepCount() }, (_, i) => i));
  readonly barProgress = computed(() =>
    this.stepCount() <= 1 ? 1 : (this.stepIndex() + 1) / this.stepCount(),
  );
  readonly fadeAnimKey = computed(() => this.fadeKey());

  tooltipWidth = TOOLTIP_WIDTH;

  onNext(): void {
    this.controller.next();
  }
  onBack(): void {
    this.controller.previous();
  }
  onClose(): void {
    this.controller.exit();
  }
}

function rectEq(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}
